/**
 * Helper functions for auto-validation processing
 * Extracted to reduce cognitive complexity and improve maintainability
 */
import { getDaysAgo } from "../../shared/Utils";

/**
 * Tax ID field name variations
 */
export const TAX_ID_KEYS = [
    "taxIds",
    "tax_id",
    "taxId",
    "taxpayerIds",
    "taxNumbers",
    "tax_ids",
] as const;

/**
 * Expiration date field name from the API
 */
export const EXPIRATION_DATE_KEY = "expirationDate";

/**
 * Check if a key is a tax ID field
 */
export const isTaxIdKey = (key: string): boolean =>
    TAX_ID_KEYS.includes(key as (typeof TAX_ID_KEYS)[number]);

/**
 * Check if a key is the expiration date field
 */
export const isExpirationDateKey = (key: string): boolean => key === EXPIRATION_DATE_KEY;

/**
 * Check if a date string represents a date in the past.
 */
export const isExpiredDate = (dateStr: string): boolean => getDaysAgo(dateStr) > 0;

/**
 * Process signatures field and add to issues if empty
 */
export const processSignaturesField = (value: boolean, issues: string[]): void => {
    if (value) {
        issues.push("Signature");
    }
};

/**
 * Process a single tax ID entry
 */
export const processTaxId = (
    taxId: Record<string, unknown>,
    issues: string[],
    predictions: Record<string, unknown>,
    hasTaxFormatError: { value: boolean }
): void => {
    if (taxId.isError) {
        issues.push("Tax ID");
        if (taxId.value && typeof taxId.value === "string" && taxId.value.length > 0) {
            hasTaxFormatError.value = true;
        }
        // Only add to predictions if it has a value
        if ("value" in taxId) {
            if (!("taxIds" in predictions)) predictions.taxIds = [];
            (predictions.taxIds as unknown[]).push(taxId);
        }
    } else {
        // Always store predictions for mismatch detection
        if (!("taxIds" in predictions)) predictions.taxIds = [];
        (predictions.taxIds as unknown[]).push(taxId);
    }
};

/**
 * Process tax IDs field (handles both array and single object)
 */
export const processTaxIdsField = (
    value: unknown,
    issues: string[],
    predictions: Record<string, unknown>,
    hasTaxFormatError: { value: boolean }
): void => {
    const taxIdsArray = Array.isArray(value) ? value : [value];
    taxIdsArray.forEach(taxId =>
        processTaxId(taxId as Record<string, unknown>, issues, predictions, hasTaxFormatError)
    );
};

/**
 * Process field with error flag
 */
export const processErrorField = (
    key: string,
    value: Record<string, unknown>,
    issues: string[],
    missingFields: string[]
): void => {
    if (value.reasonPhrase) {
        issues.push(value.reasonPhrase as string);
    } else {
        missingFields.push(key);
    }
};

/**
 * Process expiration date field — checks for isError and whether the date is in the past
 */
export const processExpirationDateField = (
    key: string,
    value: Record<string, unknown>,
    issues: string[],
    predictions: Record<string, unknown>,
    missingFields: string[],
    hasExpiredDate: { value: boolean }
): void => {
    if (value.isError) {
        processErrorField(key, value, issues, missingFields);
        return;
    }
    if ("value" in value && typeof value.value === "string" && value.value.length > 0) {
        predictions[key] = value.value;
        if (isExpiredDate(value.value)) {
            hasExpiredDate.value = true;
            issues.push("Expiration Date");
        }
    }
};

/**
 * Process field with value property
 */
export const processValueField = (
    key: string,
    value: Record<string, unknown>,
    predictions: Record<string, unknown>
): void => {
    predictions[key] = value.value;
};

/**
 * Process primitive field value
 */
export const processPrimitiveField = (
    key: string,
    value: unknown,
    predictions: Record<string, unknown>
): void => {
    predictions[key] = value;
};

/**
 * Process a single validation field
 */
export const processValidationField = (
    key: string,
    value: unknown,
    issues: string[],
    predictions: Record<string, unknown>,
    missingFields: string[],
    hasTaxFormatError: { value: boolean },
    hasExpiredDate: { value: boolean }
): void => {
    if (value === null) {
        return; // Skip null values
    }

    if (key === "signaturesIsError") {
        processSignaturesField(value as boolean, issues);
    } else if (isTaxIdKey(key)) {
        processTaxIdsField(value, issues, predictions, hasTaxFormatError);
    } else if (isExpirationDateKey(key) && value && typeof value === "object") {
        processExpirationDateField(
            key,
            value as Record<string, unknown>,
            issues,
            predictions,
            missingFields,
            hasExpiredDate
        );
    } else if (value && typeof value === "object" && (value as Record<string, unknown>).isError) {
        processErrorField(key, value as Record<string, unknown>, issues, missingFields);
    } else if (value && typeof value === "object" && "value" in value) {
        processValueField(key, value as Record<string, unknown>, predictions);
    } else if (value && typeof value !== "object") {
        processPrimitiveField(key, value, predictions);
    }
};

/**
 * Format missing fields into a human-readable message
 */
export const formatMissingFieldsMessage = (missingFields: string[]): string => {
    const quotedFields = missingFields.map(key => `'${key}'`);

    if (quotedFields.length === 1) {
        return `${quotedFields[0]} is missing in your certificate`;
    }

    const lastField = quotedFields[quotedFields.length - 1];
    const remainingFields = quotedFields.slice(0, -1);
    return `${remainingFields.join(", ")} and ${lastField} are missing in your certificate`;
};

/**
 * Process all validation results and extract issues and predictions
 */
export const processValidationResults = (autoValidationResults: Record<string, unknown>) => {
    const issues: string[] = [];
    const predictions: Record<string, unknown> = {};
    const missingFields: string[] = [];
    const hasTaxFormatError = { value: false };
    const hasExpiredDate = { value: false };

    // Clean up null values and process each field
    Object.keys(autoValidationResults).forEach(key => {
        const value = autoValidationResults[key];
        if (value === null) {
            delete autoValidationResults[key];
        } else {
            processValidationField(
                key,
                value,
                issues,
                predictions,
                missingFields,
                hasTaxFormatError,
                hasExpiredDate
            );
        }
    });

    // Add missing fields message if any
    if (missingFields.length > 0) {
        issues.push(formatMissingFieldsMessage(missingFields));
    }

    return {
        issues,
        predictions,
        hasTaxFormatError: hasTaxFormatError.value,
        hasExpiredDate: hasExpiredDate.value,
    };
};

/**
 * Create validation data object
 */
export const createValidationData = (
    issues: string[],
    predictions: Record<string, unknown>,
    isMulti: boolean | undefined,
    isSinglePurchase: boolean | undefined,
    type: string,
    hasTaxFormatError: boolean,
    hasExpiredDate: boolean = false
) => ({
    status: issues.length > 0 ? "warning" : "success",
    issues,
    predictions,
    isMulti,
    isSinglePurchase,
    taskType: type,
    isInValidTaxFormat: hasTaxFormatError,
    isExpiredDate: hasExpiredDate,
});

/**
 * Handle invalid validation data
 */
export const handleInvalidData = (
    dispatch: (action: unknown) => void,
    type: string,
    onlyParse: boolean,
    setIsAutoValidatingFile: (value: boolean) => unknown,
    setAutoValidationData: (data: unknown) => unknown
): void => {
    if (!onlyParse) {
        dispatch(setIsAutoValidatingFile(false));
        dispatch(
            setAutoValidationData({
                status: "error",
                issues: ["Invalid validation data received"],
                predictions: {},
                isMulti: false,
                isSinglePurchase: false,
                taskType: type,
                isInValidTaxFormat: false,
            })
        );
    }
};
